'use client';

import { Fragment } from 'react';

import { publicEnv } from '@/lib/env';
import ActivityInfo from '@/modules/players/v2/tabs/overview/TournamentResults/ActivityInfo';
import MatchInfo from '@/modules/players/v2/tabs/overview/TournamentResults/MatchInfo';
import MatchScoresDetails from '@/modules/players/v2/tabs/overview/TournamentResults/MatchScoresDetails';
import MedalsRow from '@/modules/players/v2/tabs/overview/TournamentResults/MedalsRow';
import ResultsContainer from '@/modules/players/v2/tabs/overview/TournamentResults/ResultsContainer';
import { PlayerData, PlayerLiveGameStatusData } from '@/types/players.types';

interface TournamentResultsProps {
  data: PlayerLiveGameStatusData[];
  userId: string;
  player?: PlayerData;
}

interface GroupedData {
  [key: string]: PlayerLiveGameStatusData[];
}

export const TournamentResults = ({ data, userId, player }: TournamentResultsProps) => {
  const groupDataByActivity = (data: PlayerLiveGameStatusData[]): GroupedData => {
    return data
      ? data.reduce((groups: GroupedData, item: PlayerLiveGameStatusData) => {
          const group = groups[item.ActivityTitle] || [];
          group.push(item);
          groups[item.ActivityTitle] = group;
          return groups;
        }, {})
      : {};
  };

  const groupedData = groupDataByActivity(data);

  if (Object.keys(groupedData).length === 0) {
    return (
      <div className='mb-4 flex w-full justify-center rounded-lg border bg-white shadow-md md:w-1/2'>
        <div className='p-8 text-gray-900'>No recent tournament results found for this player.</div>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <div className='mb-3 flex flex-col justify-between rounded-t-lg bg-gray-700 p-3 text-white sm:flex-row'>
        <p>{data?.[0]?.EventTitle}</p>
      </div>
      {Object.entries(groupedData).map(([activityTitle, matches]) => {
        const matchWithDate = matches.find((match) => match.DateOfActivity);
        const BRACKETS_REDIRECT_URL = `${publicEnv.NEXT_PUBLIC_BRACKETS_URL}/tournaments/${matchWithDate?.EventID}/events/${matchWithDate?.ActivityID}`;

        return (
          <div className='mb-3' key={activityTitle}>
            <ActivityInfo
              activityTitle={activityTitle}
              goLiveStatusId={matchWithDate?.GoLiveStatusID}
              bracketsURL={BRACKETS_REDIRECT_URL}
            />
            {matches.map((match, index) => (
              <Fragment key={`${match.ActivityID} - ${index}`}>
                <div
                  className={`flex min-h-[128px] flex-col border-x border-b border-gray-200 sm:flex-row ${
                    match.InBracketType === 'B' ||
                    match.InBracketType === 'GS' ||
                    match.InBracketType === 'GS2'
                      ? 'bg-purple-100'
                      : ''
                  }`}
                >
                  <MatchInfo match={match} />
                  <MatchScoresDetails match={match} userId={userId} />
                  <ResultsContainer
                    scoreId={match.ScoreID}
                    bracketType={match.InBracketType}
                    bracketsURL={BRACKETS_REDIRECT_URL}
                  />
                </div>
                {index === matches.length - 1 && (
                  <MedalsRow
                    noMedalsWasAwarded={match.NoMedalWasAwarded}
                    gold={match.GoldMedal_Team}
                    silver={match.SilverMedal_Team}
                    bronze={match.BronzeMedal_Team}
                    player={player}
                  />
                )}
              </Fragment>
            ))}
          </div>
        );
      })}
    </div>
  );
};
